import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import sdApi from './sdApi';
import Txt2Img from './Txt2Img';
import Img2Img from './Img2Img';
import Gallery from './Gallery';
import './StableDiffusionPage.css';

function StableDiffusionPage() {
  const { isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState('txt2img');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState('');
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const [loras, setLoras] = useState([]);
  const [isLoadingLoras, setIsLoadingLoras] = useState(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const options = await sdApi.getOptions();
        setIsConnected(true);
        if (options.sd_model_checkpoint) {
          setCurrentModel(options.sd_model_checkpoint);
        }
      } catch (err) {
        setIsConnected(false);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const modelList = await sdApi.getModels();
        setModels(modelList);
      } catch (err) {}
    };

    const fetchLoras = async () => {
      try {
        const loraList = await sdApi.getLoras();
        setLoras(loraList);
      } catch (err) {}
    };

    if (isConnected) {
      fetchModels();
      fetchLoras();
    }
  }, [isConnected]);

  const handleRefreshLoras = async () => {
    setIsLoadingLoras(true);
    try {
      await sdApi.refreshLoras();
      const loraList = await sdApi.getLoras();
      setLoras(loraList);
    } catch (err) {
      console.error('刷新 Lora 失败:', err);
    } finally {
      setIsLoadingLoras(false);
    }
  };

  const handleModelChange = async (modelName) => {
    if (modelName === currentModel || isLoadingModel) return;
    
    setIsLoadingModel(true);
    try {
      await sdApi.setModel(modelName);
      setCurrentModel(modelName);
    } catch (err) {
      console.error('切换模型失败:', err);
    } finally {
      setIsLoadingModel(false);
    }
  };

  const handleImageGenerated = (imageData) => {
    setGeneratedImages(prev => [imageData, ...prev]);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'txt2img':
        return <Txt2Img onImageGenerated={handleImageGenerated} loras={loras} onRefreshLoras={handleRefreshLoras} isLoadingLoras={isLoadingLoras} />;
      case 'img2img':
        return <Img2Img onImageGenerated={handleImageGenerated} loras={loras} onRefreshLoras={handleRefreshLoras} isLoadingLoras={isLoadingLoras} />;
      case 'gallery':
        return <Gallery images={generatedImages} />;
      default:
        return <Txt2Img onImageGenerated={handleImageGenerated} loras={loras} onRefreshLoras={handleRefreshLoras} isLoadingLoras={isLoadingLoras} />;
    }
  };

  return (
    <div className="sd-page">
      <div className="sd-bg">
        <div className="sd-bg-shape shape-1"></div>
        <div className="sd-bg-shape shape-2"></div>
        <div className="sd-bg-lines"></div>
      </div>

      <header className="sd-header">
        <div className="sd-header-content">
          <div className="sd-header-left">
            <Link to="/" className="sd-back-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              返回
            </Link>
            <div className="sd-logo">
              <div className="sd-logo-icon">🎨</div>
              <div>
                <h1>Stable Diffusion</h1>
                <span>AI 图像生成工作室</span>
              </div>
            </div>
          </div>

          <nav className="sd-nav-tabs">
            <button className={`sd-nav-tab ${activeTab === 'txt2img' ? 'active' : ''}`} onClick={() => setActiveTab('txt2img')}>
              ✨ 文生图
            </button>
            <button className={`sd-nav-tab ${activeTab === 'img2img' ? 'active' : ''}`} onClick={() => setActiveTab('img2img')}>
              🖼️ 图生图
            </button>
            <button className={`sd-nav-tab ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>
              🎨 画廊 ({generatedImages.length})
            </button>
          </nav>

          <div className="sd-header-right">
            <div className="sd-model-selector">
              <label>🧠 模型</label>
              <select 
                value={currentModel} 
                onChange={(e) => handleModelChange(e.target.value)}
                disabled={!isConnected || isLoadingModel}
              >
                {models.length > 0 ? (
                  models.map(model => (
                    <option key={model.title} value={model.title}>{model.model_name}</option>
                  ))
                ) : (
                  <option value="">加载中...</option>
                )}
              </select>
              {isLoadingModel && <span className="sd-model-loading">切换中...</span>}
            </div>

            <div className="sd-status">
              <span className={`sd-status-dot ${isConnected ? 'connected' : ''}`}></span>
              {isConnected ? 'API 已连接' : 'API 未连接'}
            </div>

            {isAuthenticated ? (
              <Link to="/dashboard" className="sd-user-link">{user?.real_name || user?.username}</Link>
            ) : (
              <Link to="/login" className="sd-login-btn">登录</Link>
            )}
          </div>
        </div>
      </header>

      <main className="sd-main">
        {renderContent()}
      </main>
    </div>
  );
}

export default StableDiffusionPage;

